
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Calendar, 
  Users, 
  Clock, 
  BookOpen,
  CheckCircle,
  ArrowRight,
  Target,
  Award,
  Microscope,
  Cpu,
  Wrench,
  Palette,
  Calculator,
  Lightbulb,
  Rocket,
  Globe,
  Brain,
  Zap,
  Star,
  Trophy
} from 'lucide-react';

const AfterschoolProgram = () => {
  const enrichmentPrograms = [
    {
      name: 'Science Explorers',
      description: 'Journey through the wonders of science with hands-on experiments and discoveries',
      icon: Microscope,
      color: 'from-green-400 to-emerald-600',
      features: [
        'Interactive laboratory experiments',
        'Nature exploration and observation',
        'Scientific method development',
        'Environmental science projects'
      ],
      image: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      name: 'Engineering Enthusiasts',
      description: 'Build, design, and create innovative solutions to real-world challenges',
      icon: Wrench,
      color: 'from-orange-400 to-red-600',
      features: [
        'Structural engineering projects',
        'Problem-solving methodologies',
        'Design thinking processes',
        'Mechanical building challenges'
      ],
      image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      name: 'Art Innovators',
      description: 'Blend creativity with technology to express ideas through digital and traditional art',
      icon: Palette,
      color: 'from-purple-400 to-pink-600',
      features: [
        'Digital art and design',
        'Creative multimedia projects',
        'Artistic expression techniques',
        'Visual storytelling methods'
      ],
      image: 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      name: 'Maths Masters',
      description: 'Explore mathematical concepts through real-world applications and problem-solving',
      icon: Calculator,
      color: 'from-indigo-400 to-purple-600',
      features: [
        'Advanced mathematical reasoning',
        'Statistical analysis projects',
        'Mathematical modelling',
        'Logic and pattern recognition'
      ],
      image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    }
  ];

  const programBenefits = [
    {
      title: 'Innovative Learning Environment',
      description: 'State-of-the-art facilities equipped with the latest technology and learning tools',
      icon: Lightbulb
    },
    {
      title: 'Expert Instructors',
      description: 'Qualified educators passionate about enrichment learning and student development',
      icon: Users
    },
    {
      title: 'Small Group Focus',
      description: 'Personalised attention with maximum 8 students per group for optimal learning',
      icon: Target
    },
    {
      title: 'Project-Based Learning',
      description: 'Hands-on projects that develop critical thinking and problem-solving skills',
      icon: Brain
    },
    {
      title: 'Future-Ready Skills',
      description: 'Preparation for careers in science, engineering, arts, and mathematics',
      icon: Rocket
    },
    {
      title: 'Confidence Building',
      description: 'Encouraging environment that builds self-confidence and leadership skills',
      icon: Trophy
    }
  ];

  const missionValues = [
    'Inspire curiosity and wonder about the world',
    'Develop critical thinking and problem-solving abilities',
    'Foster creativity and innovative thinking',
    'Build collaboration and teamwork skills',
    'Prepare students for future study and careers',
    'Create confident, capable, and creative learners'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Afterschool Enrichment Programme | Science, Engineering, Arts & Maths | Schoolola</title>
        <meta name="description" content="Join our innovative Afterschool Enrichment programme featuring Science Explorers, Engineering Enthusiasts, Art Innovators & Maths Masters. Inspiring the next generation of innovators." />
        <meta name="keywords" content="afterschool enrichment, science explorers, engineering enthusiasts, art innovators, maths masters, enrichment education, innovative learning" />
      </Helmet>

      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-50 via-white to-accent-50 py-24 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-r from-green-400/20 to-blue-400/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-orange-400/10 to-pink-400/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto text-center space-y-12">
            <div className="space-y-6">
              <Badge className="bg-gradient-to-r from-accent to-primary text-white px-8 py-3 text-lg rounded-full shadow-lg">
                Afterschool Enrichment Programme
              </Badge>
              <h1 className="text-5xl lg:text-7xl font-heading font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent leading-tight">
                Inspiring Innovation
              </h1>
              <p className="text-2xl lg:text-3xl font-semibold text-gray-700 max-w-4xl mx-auto">
                Science Explorers • Tech Whizzes • Engineering Enthusiasts • Art Innovators • Maths Masters
              </p>
            </div>
            
            <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border border-white/40 max-w-4xl mx-auto">
              <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                To inspire the next generation of innovators, creators, and leaders by providing fun and interactive 
                Enrichment learning that develops critical thinking, creativity, and problem-solving skills essential 
                for success in the 21st century.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/book-assessment">
                <Button size="lg" className="bg-gradient-to-r from-accent to-primary hover:from-primary hover:to-accent text-white text-xl px-10 py-4 rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105">
                  Begin the Journey
                  <Rocket className="ml-3 h-6 w-6" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" size="lg" className="text-xl px-10 py-4 rounded-2xl border-2 border-primary/30 hover:bg-primary/5 transition-all">
                  Discover More
                  <Globe className="ml-3 h-6 w-6" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Enrichment Programs Grid */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-6 mb-20">
              <h2 className="text-4xl lg:text-5xl font-heading font-bold text-gray-900">
                Our Enrichment Courses
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Five specialised courses designed to ignite passion for learning and discovery
              </p>
            </div>

            <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8">
              {enrichmentPrograms.map((program, index) => (
                <Card key={index} className="group border-0 shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden bg-white/90 backdrop-blur-sm hover:bg-white">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={program.image}
                      alt={program.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${program.color} opacity-80 group-hover:opacity-70 transition-opacity`}></div>
                    <div className="absolute top-4 right-4">
                      <div className="bg-white/20 backdrop-blur-md rounded-full p-3 border border-white/30">
                        <program.icon className="h-8 w-8 text-white" />
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-2xl font-heading font-bold text-white mb-2">{program.name}</h3>
                    </div>
                  </div>
                  <CardContent className="p-8 space-y-6">
                    <p className="text-gray-600 leading-relaxed text-lg">{program.description}</p>
                    <ul className="space-y-3">
                      {program.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Program Benefits */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-6 mb-20">
              <h2 className="text-4xl lg:text-5xl font-heading font-bold text-gray-900">
                Why Choose Our Enrichment Programme?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive benefits that prepare students for future success
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {programBenefits.map((benefit, index) => (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 group">
                  <CardContent className="p-8 text-center space-y-4">
                    <div className="h-16 w-16 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform shadow-lg">
                      <benefit.icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-24 bg-gradient-to-r from-primary to-accent">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-12">
            <h2 className="text-4xl lg:text-5xl font-heading font-bold text-white">
              Our Values & Mission
            </h2>
            <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20">
              <div className="grid md:grid-cols-2 gap-6">
                {missionValues.map((value, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Star className="h-5 w-5 text-yellow-300 flex-shrink-0" />
                    <span className="text-white font-medium">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programme Details */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-2xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-primary to-accent text-white p-10">
                <div className="text-center space-y-4">
                  <CardTitle className="text-3xl font-heading">
                    Programme Information
                  </CardTitle>
                  <p className="text-primary-100 text-lg">
                    Comprehensive afterschool enrichment programme
                  </p>
                </div>
              </CardHeader>
              <CardContent className="p-10 space-y-8">
                <div className="grid md:grid-cols-3 gap-8">
                  <div className="text-center space-y-3">
                    <Clock className="h-10 w-10 text-primary mx-auto" />
                    <div className="font-semibold text-lg">Duration</div>
                    <div className="text-gray-600">2 hours per session</div>
                  </div>
                  <div className="text-center space-y-3">
                    <Calendar className="h-10 w-10 text-primary mx-auto" />
                    <div className="font-semibold text-lg">Schedule</div>
                    <div className="text-gray-600">Mon-Fri 3:30-5:30 PM</div>
                  </div>
                  <div className="text-center space-y-3">
                    <Users className="h-10 w-10 text-primary mx-auto" />
                    <div className="font-semibold text-lg">Group Size</div>
                    <div className="text-gray-600">Maximum 8 students</div>
                  </div>
                </div>

                <div className="border-t pt-8">
                  <h4 className="font-semibold text-gray-900 mb-6 text-center text-xl">What's Included</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      'All materials and equipment provided',
                      'Qualified enrichment programme instructors',
                      'Project portfolios and certificates',
                      'Regular progress reports to parents',
                      'Access to cutting-edge technology',
                      'Collaborative learning environment'
                    ].map((item, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="text-center pt-8">
                  <Link to="/book-assessment">
                    <Button size="lg" className="bg-gradient-to-r from-accent to-primary text-white text-lg px-12 py-4 rounded-2xl">
                      Enrol Your Child Today
                      <ArrowRight className="ml-3 h-6 w-6" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-primary via-accent to-primary-600">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto space-y-12">
            <div className="space-y-6">
              <h2 className="text-4xl lg:text-5xl font-heading font-bold text-white">
                Ready to Ignite Your Child's Potential?
              </h2>
              <p className="text-xl text-white/90 leading-relaxed max-w-3xl mx-auto">
                Join our innovative Afterschool Enrichment Programme and watch your child develop into a confident, 
                creative, and capable innovator ready for the challenges of tomorrow.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/book-assessment">
                <Button size="lg" className="bg-white text-primary hover:bg-gray-100 font-semibold text-xl px-12 py-4 rounded-2xl shadow-xl hover:shadow-2xl transition-all transform hover:scale-105">
                  Book Free Assessment
                  <Zap className="ml-3 h-6 w-6" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" size="lg" className="text-xl px-12 py-4 border-2 border-white text-white hover:bg-white hover:text-primary rounded-2xl backdrop-blur-md transition-all font-medium">
                  Contact Our Team
                  <ArrowRight className="ml-3 h-6 w-6" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AfterschoolProgram;
